/**
 * Phase 22.3 — Reputation / Review Funnel.
 *
 * A top-level tab, not a child of Intelligence. Review sends are a different unit
 * of measurement from post performance: no model predicts them, no engagement rate
 * applies, and nothing here is scored. Sitting it beside Analytics would invite
 * exactly the framing bleed the ticket rules out.
 *
 * That intent is carried visually as well as structurally — no gradient heading, no
 * charts, no metric-card row of engagement stats. It is a log, and it reads as one.
 *
 * WHAT THE NUMBERS MEAN. Every row is a review request *initiated* from the Review
 * Request Tool. Not sent, not delivered, not read. The tool hands off to the OS
 * messaging app and loses visibility, so no send confirmation exists to display.
 * The banner says so on the page rather than only in this comment, because a count
 * of "sends" with no caveat is precisely how a log like this gets misread later.
 *
 * The GBP correlation section (22.6) sits above the log and only for a single client;
 * it is two counts side by side, never attribution. See pages/reputation/ReviewCorrelation.
 *
 * The customer column shows the minimised display label ("Jane D."). The dedup hash
 * is never fetched — see ReviewSendRow in api/v1/review_sends.py.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { RateReview } from '@mui/icons-material';

import { reputationAPI } from '../services/api';
import { useClients } from '../contexts/ClientContext';
import { ClientSelector, PageHeader } from '../components/primitives';
import ReviewCorrelation from './reputation/ReviewCorrelation';

const ROWS_PER_PAGE = 25;

/** "first" / "repeat" carry different meaning to the person making the call. */
function VariantChip({ variant, index }) {
  const isRepeat = variant === 'repeat';
  return (
    <Tooltip title={isRepeat ? 'Follow-up ask to a returning customer' : 'First time this customer was asked'}>
      <Chip
        size="small"
        label={`${isRepeat ? 'Repeat' : 'First-time'} · v${index}`}
        variant="outlined"
        sx={{
          borderColor: isRepeat ? 'rgba(210, 168, 255, 0.4)' : 'rgba(126, 231, 135, 0.4)',
          color: isRepeat ? '#d2a8ff' : '#7ee787',
        }}
      />
    </Tooltip>
  );
}

/** Which of the tool's two hand-off paths fired. */
function ChannelChip({ channel }) {
  const isDeepLink = channel === 'deep_link';
  return (
    <Tooltip title={isDeepLink ? 'Opened the messaging app directly' : 'Text copied to clipboard to paste manually'}>
      <Chip
        size="small"
        label={isDeepLink ? 'Deep link' : 'Copied'}
        variant="outlined"
        sx={{ borderColor: 'rgba(139, 148, 158, 0.4)', color: '#8b949e' }}
      />
    </Tooltip>
  );
}

function formatWhen(iso) {
  if (!iso) return '—';
  const d = new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

export default function Reputation() {
  const { clients: clientsList } = useClients();
  const [clientId, setClientId] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await reputationAPI.getReviewSends({
        clientId,
        limit: ROWS_PER_PAGE,
        offset: page * ROWS_PER_PAGE,
      });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      // Say what failed. An empty table with no explanation reads as "no sends yet",
      // which is a different and much worse thing than "the request errored".
      setError(e?.response?.data?.detail || e.message || 'Could not load the send log');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [clientId, page]);

  useEffect(() => {
    load();
  }, [load]);

  const showingAll = clientId == null;

  return (
    <Box>
      <PageHeader
        title="Reputation"
        subtitle="Review requests initiated from the Review Request Tool"
        icon={<RateReview sx={{ mr: 1, color: '#7ee787' }} />}
        actions={
          <ClientSelector
            clients={clientsList}
            value={clientId}
            onChange={(client) => {
              setClientId(client ? client.id : null);
              setPage(0);
            }}
            allowEmpty
            emptyLabel="All clients"
            label="Client"
          />
        }
      />

      {/* The one thing this page must never be misread as. */}
      <Alert severity="info" sx={{ mb: 3 }}>
        These are requests <strong>initiated</strong>, not confirmed deliveries. The tool
        hands off to the phone&apos;s messaging app, so there is no signal for whether a
        text was actually sent, delivered or read — and no review is attributed to any
        individual request.
      </Alert>

      {error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : null}

      {/* One client at a time. Adding up several clients' reviews against several
          clients' requests would compare two unrelated populations. */}
      {showingAll ? null : (
        <ReviewCorrelation
          clientId={clientId}
          clientName={clientsList?.find((c) => c.id === clientId)?.name}
        />
      )}

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(139, 148, 158, 0.2)' }}
        >
          <Typography variant="subtitle2" sx={{ color: '#8b949e' }}>
            Send log
          </Typography>
          <Typography variant="caption" sx={{ color: '#8b949e' }}>
            {loading ? 'Loading…' : `${total} request${total === 1 ? '' : 's'} logged`}
            {showingAll ? '' : ' for this client'}
          </Typography>
        </Stack>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ py: 6, px: 3, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#8b949e' }}>
              {error
                ? 'The send log could not be loaded.'
                : 'No review requests logged yet.'}
            </Typography>
            {!error ? (
              <Typography variant="caption" sx={{ color: '#6e7681', display: 'block', mt: 1 }}>
                Rows appear here once the Review Request Tool starts posting send events.
              </Typography>
            ) : null}
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>Customer</TableCell>
                    {showingAll ? <TableCell>Client</TableCell> : null}
                    <TableCell>Template</TableCell>
                    <TableCell>Channel</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>
                        <Tooltip title={`Recorded by Nexus at ${formatWhen(r.created_at)}`}>
                          <span>{formatWhen(r.initiated_at)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{r.customer_label || '—'}</TableCell>
                      {showingAll ? (
                        <TableCell sx={{ color: '#8b949e' }}>{r.client_name || '—'}</TableCell>
                      ) : null}
                      <TableCell>
                        <VariantChip variant={r.template_variant} index={r.variant_index} />
                      </TableCell>
                      <TableCell>
                        <ChannelChip channel={r.channel} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, next) => setPage(next)}
              rowsPerPage={ROWS_PER_PAGE}
              rowsPerPageOptions={[ROWS_PER_PAGE]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
